import { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useRecoilState } from 'recoil';

import { geocodeAddress, searchZipcodes } from '@/repositories/HttpRepository';

import ReactMapGL from 'react-map-gl/maplibre';
import { produce } from 'immer';

import { saveAs } from 'file-saver';

import { pickupAddressState, zipcodesState, selectedZipcodeState } from './States';
import useStyles from './styles';
import LoadingButton from '@/components/Button/LoadingButton';
import useMapLayers from './MapLayer';
import DeckGLOverlay from '@/components/DeckGLOverlay';

const mapStyle = `${import.meta.env.REACT_APP_MAP_TILER_URI}?key=${import.meta.env.REACT_APP_MAP_TILER_KEY}`;

export default function ZipCodeScreen() {
  const [geocoding, setGeocoding] = useState(false);
  const [searchingZipcode, setSearchingZipcode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [displayAllZipcode, setDisplayAllZipcode] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [requestError, setRequestError] = useState(null);

  const [viewport, setViewport] = useState({
    latitude: 37.7160978,
    longitude: -122.1832436,
    zoom: 10,
  });

  const [pickupAddress, setPickupAddress] = useRecoilState(pickupAddressState);
  const [zipcodes, setZipcodes] = useRecoilState(zipcodesState);
  const [selectedZipcode, setSelectedZipcode] = useRecoilState(selectedZipcodeState);

  const layers = useMapLayers();

  const { classes } = useStyles();

  /*
  useEffect(() => {
    setViewport(produce(viewport, (draftState) => {
      draftState.zoom = radiusToZoom();
    }));
  }, [pickupAddress.radius]);
  */

  const updateAddress = (name, idx) => (e) => {
    let error = null;
    let parsedRadius = 0;

    if (name === 'radius') {
      if (e.target.value) {
        const rad = parseFloat(e.target.value);
        if (Number.isNaN(rad)) {
          error = 'Invalid radius number!';
        } else if (rad > 100) {
          error = 'Please input number < 100 (miles) for radius!';
        } else {
          parsedRadius = rad;
        }
      }
      setValidationError(error);
    }

    setPickupAddress((prev) =>
      produce(prev, (draft) => {
        if (name === 'radius') {
          draft[name][idx] = parsedRadius;
        } else {
          draft[name] = e.target.value;
        }
      }),
    );
  };

  const radiusToZoom = () => {
    const DEFAULT_ZOOM = 14;
    if (!pickupAddress?.radius || pickupAddress.radius.length === 0) {
      return DEFAULT_ZOOM;
    }
    const maxRadius = Math.max(...pickupAddress.radius);
    if (maxRadius <= 0) {
      return DEFAULT_ZOOM;
    }
    const zoom = Math.round(14 - Math.log(maxRadius) / Math.LN2);
    return Math.max(1, Math.min(zoom, 20));
  };

  const onSelectZipcode = (zipcode) => (e) => {
    if (selectedZipcode && selectedZipcode.properties.zipcode === zipcode.properties.zipcode) {
      setSelectedZipcode(null);
    } else {
      setSelectedZipcode(zipcode);
    }
  };

  const geocode = (e) => {
    setGeocoding(true);
    setRequestError(null);
    geocodeAddress(pickupAddress)
      .then((resp) => {
        if (resp.ok) {
          setPickupAddress((prev) =>
            produce(prev, (draft) => {
              draft.lng = resp.data.longitude;
              draft.lat = resp.data.latitude;
            }),
          );
          setViewport((prev) =>
            produce(prev, (draft) => {
              draft.latitude = resp.data.latitude;
              draft.longitude = resp.data.longitude;
              draft.zoom = radiusToZoom();
            }),
          );
        } else {
          setRequestError('Failed to geocode address');
        }
      })
      .catch(() => {
        setRequestError('Failed to geocode address');
      })
      .finally(() => {
        setGeocoding(false);
      });
  };

  const doSearchZipcode = () => {
    setSearchingZipcode(true);
    setRequestError(null);
    searchZipcodes(pickupAddress, pickupAddress.radius, pickupAddress.distance_type, pickupAddress.distance_point)
      .then((resp) => {
        if (resp.ok) {
          setZipcodes(resp.data);
          setSelectedZipcode(null);
        } else {
          setRequestError('Failed to search zipcodes');
        }
      })
      .catch(() => {
        setRequestError('Failed to search zipcodes');
      })
      .finally(() => {
        setSearchingZipcode(false);
      });
  };

  const downloadZipcodes = () => {
    setDownloading(true);
    setRequestError(null);
    const filenameBase = pickupAddress.street || pickupAddress.city || pickupAddress.state || pickupAddress.zipcode || 'export';
    const filename = filenameBase
      .trim()
      .toLowerCase()
      .replace(/[^a-zA-Z0-9_]/g, '-');

    const zoneStrings = zipcodes.map((zoneZipcode, idx) => {
      const zoneRadius = Array.isArray(pickupAddress.radius) ? pickupAddress.radius[idx] : pickupAddress.radius;
      const addressLine = `${pickupAddress.street || ''}, ${pickupAddress.city || ''}, ${pickupAddress.state || ''} ${pickupAddress.zipcode || ''}`.trim();
      const zipcodeList = zoneZipcode.map((z) => z.properties.zipcode).join(', ');

      return `Address: ${addressLine}
Distance Type: ${pickupAddress.distance_type}
Point: ${pickupAddress.distance_point}
Zone: ${idx + 1}
Radius: ${zoneRadius} (miles)
Zipcodes: ${zipcodeList}`;
    });

    const finalContent = zoneStrings.join('\n\n');
    const radiusForFilename = Array.isArray(pickupAddress.radius)
      ? pickupAddress.radius.join('-')
      : pickupAddress.radius;

    const file = new File(
      [finalContent],
      `zipcodes-${filename}-${radiusForFilename}-${pickupAddress.distance_type}-${pickupAddress.distance_point}.txt`,
      { type: 'text/plain;charset=utf-8' },
    );
    saveAs(file);
    setDownloading(false);
  };

  const handleViewStateChange = useCallback(({ viewState }) => {
    setViewport(viewState);
  }, []);

  const zipcodeLength = zipcodes?.length ? zipcodes.map((z) => z.length).reduce((a, b) => a + b) : 0;
  const displayZipcodes = displayAllZipcode ? zipcodes : zipcodes.map((z) => z.slice(0, 5));
  const canGeocode = pickupAddress.street && pickupAddress.city && pickupAddress.state && pickupAddress.zipcode;

  return (
    <Container style={{ padding: 20 }}>
      <Box py={2} px={1}>
        <Typography variant="h2" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          Search Zipcode
        </Typography>
      </Box>
      <form className={classes.inputForm}>
        <Grid container direction="row" justifyContent="space-between">
          <Grid size={10}>
            <TextField
              id="street"
              margin="dense"
              variant="outlined"
              onChange={updateAddress('street')}
              className={classes.large}
              label="Street"
              value={pickupAddress.street}
              size='small'
            />
            <TextField
              id="city"
              margin="dense"
              variant="outlined"
              onChange={updateAddress('city')}
              className={classes.small}
              label="City"
              value={pickupAddress.city}
              size='small'
            />
            <TextField
              id="state"
              margin="dense"
              variant="outlined"
              onChange={updateAddress('state')}
              className={classes.small}
              label="State"
              value={pickupAddress.state}
              size='small'
            />
            <TextField
              id="zipcode"
              margin="dense"
              variant="outlined"
              onChange={updateAddress('zipcode')}
              className={classes.small}
              label="Zipcode"
              value={pickupAddress.zipcode}
              size='small'
            />
          </Grid>
          <Grid size={2}>
            <Box>
              <LoadingButton
                disabled={searchingZipcode || !canGeocode}
                color="primary"
                loading={geocoding}
                onClick={geocode}
                variant="contained"
                label="Geocode"
              />
            </Box>
          </Grid>
        </Grid>
        {pickupAddress.lat && pickupAddress.lng && (
          <Box>
            <Grid container justifyContent="flex-start" style={{ marginTop: 20 }}>
              <Grid size={3}>
                {pickupAddress.radius &&
                  pickupAddress.radius.map((rad, idx) => (
                    <Grid key={`radius-${idx}`} container alignItems="center">
                      <TextField
                        type="number"
                        variant="outlined"
                        margin="dense"
                        onChange={updateAddress('radius', idx)}
                        value={rad}
                        className={classes.large}
                        label={`Radius`}
                        size='small'
                      />
                      <span style={{ color: '#999' }}>Z{idx + 1} (miles)</span>
                    </Grid>
                  ))}
              </Grid>
              <Grid size={3}>
                <FormControl margin="dense" variant="outlined" size="small">
                  <InputLabel id="distance-type-label">Distance Type</InputLabel>
                  <Select
                    labelId="distance-type-label"
                    id="distance-type"
                    variant="outlined"
                    margin="dense"
                    label="Distance Type"
                    value={pickupAddress.distance_type}
                    onChange={updateAddress('distance_type')}
                  >
                    <MenuItem value="FLYING_DISTANCE">Flying Distance</MenuItem>
                    <MenuItem value="DRIVING_DISTANCE">Driving Distance</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={3}>
                <FormControl variant="outlined" margin="dense" size="small">
                  <InputLabel id="distance-point-label">Distance Point</InputLabel>
                  <Select
                    labelId="distance-point-label"
                    id="distance-point"
                    label="Distance Point"
                    variant="outlined"
                    margin="dense"
                    value={pickupAddress.distance_point}
                    onChange={updateAddress('distance_point')}
                  >
                    <MenuItem value="CLOSEST">Closest Point</MenuItem>
                    <MenuItem value="FARTHEST">Farthest Point</MenuItem>
                    <MenuItem value="CENTER">Center Point</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box>
              <LoadingButton
                disabled={!(pickupAddress?.lat && pickupAddress?.lng && pickupAddress?.radius) || !!validationError}
                color="primary"
                loading={searchingZipcode}
                onClick={doSearchZipcode}
                variant="contained"
                label="Search Zipcode"
              />
              {validationError && (
                <Box pl={1}>
                  <strong style={{ color: 'red' }}>{validationError}</strong>
                </Box>
              )}
              {requestError && (
                <Box pl={1}>
                  <strong style={{ color: 'red' }}>{requestError}</strong>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </form>
      {!!zipcodeLength && (
        <Box>
          <Box pt={1} pl={1}>
            <strong>
              Found <span style={{ color: 'red' }}>{zipcodeLength}</span> zipcodes
            </strong>
            <LoadingButton
              color="primary"
              loading={downloading}
              onClick={downloadZipcodes}
              variant="contained"
              label="Download"
            />
          </Box>
          <Box display="flex">
            {displayZipcodes.map((zoneZipcode, idx) => (
              <Box key={`zone-${idx}`} px={2}>
                <Box py={2}>
                  Zone {idx + 1} - <small>{zipcodes[idx].length} items</small>
                </Box>
                <Box display="flex" flexWrap="wrap">
                  {zoneZipcode.map((zipcode) => (
                    <Button
                      key={zipcode.properties.zipcode}
                      disableElevation
                      variant={
                        selectedZipcode && selectedZipcode.properties.zipcode === zipcode.properties.zipcode
                          ? 'outlined'
                          : 'text'
                      }
                      color={
                        selectedZipcode && selectedZipcode.properties.zipcode === zipcode.properties.zipcode
                          ? 'secondary'
                          : 'inherit'
                      }
                      className={classes.zipcodeText}
                      onClick={onSelectZipcode(zipcode)}
                    >
                      {zipcode.properties.zipcode}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
          <Box py={1}>
            {zipcodeLength > 5 && !displayAllZipcode && (
              <Button disableElevation color="primary" variant="outlined" onClick={() => setDisplayAllZipcode(true)}>
                See all
              </Button>
            )}
            {zipcodeLength > 5 && displayAllZipcode && (
              <Button disableElevation color="primary" variant="outlined" onClick={() => setDisplayAllZipcode(false)}>
                See less
              </Button>
            )}
          </Box>
        </Box>
      )}
      {pickupAddress.lat && pickupAddress.lng && (
        <Box py={2}>
          <ReactMapGL
            {...viewport}
            mapStyle={mapStyle}
            onMove={handleViewStateChange}
            attributionControl={false}
            style={{ border: '1px solid #ccc', height: '600px', width: '100%' }}
          >
            <DeckGLOverlay layers={layers} getCursor={() => 'grab'} />
          </ReactMapGL>
        </Box>
      )}
    </Container>
  );
}
